import {
  ChartAssetData,
  ThresholdDirection,
  ThresholdInterval,
  Timeseries,
} from '@toggle/chart';
import {
  DetailedInsight,
  Entity,
  formatTicker,
  SnakeMeta,
  SnakeMetaThreshold,
} from '@toggle/toggle';

interface CreateChartDataItemProps {
  entity: Entity;
  ts: Timeseries;
  lineColorToken: string;
  primaryAsset?: ChartAssetData;
  isHidden?: ChartAssetData['isHidden'];
  snakeMeta?: SnakeMeta;
  thresholdIntervals?: ChartAssetData['thresholdIntervals'];
  episodes?: DetailedInsight['episodes'];
}

export const createThresholdIntervals = <
  T extends Array<{ close: number; time: string }>
>({
  ts,
  primaryAsset,
  threshold,
}: {
  ts: T;
  primaryAsset: ChartAssetData;
  threshold: Required<SnakeMetaThreshold>;
}) => {
  const intervals: ThresholdInterval[] = [];
  const tsByTime = primaryAsset.tsByTime;
  const positiveCheck =
    threshold.good > threshold.bad
      ? (item: T[0]) => item.close >= threshold.good
      : (item: T[0]) => item.close <= threshold.good;
  const negativeCheck =
    threshold.good < threshold.bad
      ? (item: T[0]) => item.close >= threshold.bad
      : (item: T[0]) => item.close <= threshold.bad;
  const mapping = {
    positive: threshold.good,
    negative: threshold.bad,
  };
  let start: number | undefined = undefined;
  let direction: ThresholdDirection | undefined = undefined;

  // TODO  fix consistent-type-assertions
  const addInterval = (item: T[0], variant: ThresholdDirection) => {
    const startIdx = start as number;
    intervals.push({
      direction: variant,
      start: startIdx,
      end: tsByTime.get(item.time) as number,
      threshold: mapping[variant as 'positive' | 'negative'],
    });
    direction = undefined;
    start = undefined;
  };

  ts.forEach(item => {
    if (positiveCheck(item)) {
      if (direction === 'negative') {
        addInterval(item, direction);
      }

      direction = 'positive';
      start = start ?? tsByTime.get(item.time);
    } else if (negativeCheck(item)) {
      if (direction === 'positive') {
        addInterval(item, direction);
      }

      direction = 'negative';
      start = start ?? tsByTime.get(item.time);
    } else if (direction) {
      addInterval(item, direction);
    }
  });

  if (direction && start !== undefined) {
    intervals.push({
      direction,
      start: start,
      end: tsByTime.get(ts[ts.length - 1].time) ?? primaryAsset.ts.length - 1,
      threshold: mapping[direction],
    });
  }

  return intervals;
};

export const createChartDataItem = ({
  entity,
  ts,
  lineColorToken,
  primaryAsset,
  isHidden = false,
  snakeMeta,
  episodes,
}: CreateChartDataItemProps): ChartAssetData => {
  let filteredTs =
    !primaryAsset || primaryAsset.entity.default_snake === entity.default_snake
      ? ts
      : ts.filter(t => primaryAsset.tsByTime.get(t.time) !== undefined);

  if (filteredTs.length === 0) {
    filteredTs = ts;
  }

  const tsByTime = new Map();
  filteredTs.forEach((item, idx) => tsByTime.set(item.time, idx));
  // TODO  fix consistent-type-assertions
  const thresholdIntervals =
    primaryAsset && snakeMeta?.threshold
      ? createThresholdIntervals({
          primaryAsset,
          ts: filteredTs,
          threshold: snakeMeta.threshold as Required<SnakeMetaThreshold>,
        })
      : undefined;

  return {
    entity: {
      ...entity, // TODO: we should not pass everything just whats needed
      formattedTicker: formatTicker(entity),
      logo: '', // Todo:  pass logo as prop
    },
    ts: filteredTs,
    originalTs: ts,
    lineColorToken,
    tsByTime,
    isHidden,
    snakeMeta,
    thresholdIntervals,
    episodesData: episodes,
  };
};
